import { useRuntimeConfig, useCookie } from "#imports";
import { useConsentStore } from "~~/stores/consentStore";
import { useAuthStore } from "~~/stores/authStore";

/** Zdarzenia wspierane przez most P2 (POST /api/store/meta-capi/track → backend /rest/meta-capi/track). */
type MetaEvent =
  | "ViewContent" | "ViewCategory" | "ViewCart" | "Search" | "InitiateCheckout"
  | "AddShippingInfo" | "AddPaymentInfo" | "AddToWishlist" | "Contact"
  | "Newsletter" | "PageView" | "AddToCart" | "Purchase";

declare global {
  interface Window { fbq?: (...args: unknown[]) => void; _fbq?: unknown; }
}

let pixelBootstrapped = false;

/**
 * Zdarzenie odpalone ZANIM user udzielił zgody marketingowej — buforowane w pamięci
 * (nic nie wysyłamy przed zgodą) i odtwarzane, gdy zgoda zostanie udzielona (consent
 * mode). Odrzucenie zgody = bufor kasowany (patrz plugin meta-capi-consent-replay).
 * Wszystko, co potrzebne do wysyłki, zapisujemy w momencie fire() (kontekst Nuxt jest
 * wtedy dostępny), żeby replay nie musiał czytać useCookie/useRuntimeConfig.
 */
type PendingMetaEntry = {
  event: MetaEvent;
  pixelData: Record<string, unknown>;
  bridgeData: Record<string, unknown>;
  eventId: string;
  useBridge: boolean;
  sourceUrl: string;
  fbp?: string;
  fbc?: string;
  ctx: Record<string, number>;
  ts: number;
};

const pendingBuffer: PendingMetaEntry[] = [];
/** Górny limit bufora (odrzucamy najstarsze) — zabezpieczenie przed nieograniczonym wzrostem. */
const MAX_PENDING = 50;
/** Nie odtwarzamy zdarzeń starszych niż 30 min (Meta i tak przyjmuje event_time do 7 dni, ale świeżość ważniejsza). */
const PENDING_TTL_MS = 30 * 60 * 1000;

export function useMetaTracking() {
  const config = useRuntimeConfig();
  const pixelId = String((config.public as Record<string, unknown>).metaPixelId || "");
  const consent = useConsentStore();
  const auth = useAuthStore();

  function marketingAllowed(): boolean {
    return !!consent.current?.marketing;
  }

  /** Lazy bootstrap standardowego Meta Pixel — DOPIERO po zgodzie i tylko w przegladarce. */
  function ensurePixel(): boolean {
    if (typeof window === "undefined") return false;
    if (!pixelId) return false;
    if (!marketingAllowed()) return false;
    if (pixelBootstrapped && typeof window.fbq === "function") return true;

    /* eslint-disable */
    (function (f: any, b: any, e: string, v: string) {
      if (f.fbq) return; const n: any = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
      const t = b.createElement(e); t.async = true; t.src = v;
      const s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */

    (window.fbq as (...a: unknown[]) => void)("init", pixelId);
    pixelBootstrapped = true;
    return true;
  }

  function genEventId(): string {
    try {
      if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
    } catch { /* noop */ }
    return "e_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  function context(): Record<string, number> {
    const ctx: Record<string, number> = { id_lang: 1, id_currency: 1 };
    const idCustomer = Number(auth.currentUser?.id ?? 0);
    if (idCustomer > 0) ctx.id_customer = idCustomer;
    return ctx;
  }

  /**
   * Wysyla event w trybie dualnym: pixel (browser) + most CAPI (server-side), wspolny event_id.
   * Purchase: useBridge=false (CAPI leci z hooka backendu z purchase_<id_order>).
   */
  /** Faktyczna wysyłka: pixel (browser) + most CAPI (server-side). Zakłada, że zgoda JEST. */
  function dispatch(entry: PendingMetaEntry): void {
    // 1) Pixel (browser) — eventID w 4. argumencie.
    if (ensurePixel() && typeof window.fbq === "function") {
      window.fbq("track", entry.event, entry.pixelData, { eventID: entry.eventId });
    }

    // 2) Most CAPI (server-side). Fire-and-forget; bledy nie moga psuc UX.
    if (entry.useBridge) {
      $fetch("/api/store/meta-capi/track", {
        method: "POST",
        credentials: "include",
        body: {
          event: entry.event,
          event_id: entry.eventId,
          event_source_url: entry.sourceUrl,
          fbp: entry.fbp,
          fbc: entry.fbc,
          consent: { marketing: true },
          context: entry.ctx,
          data: entry.bridgeData,
        },
      }).catch(() => { /* tracking nie blokuje UX */ });
    }
  }

  /**
   * Wysyla event w trybie dualnym: pixel (browser) + most CAPI (server-side), wspolny event_id.
   * Purchase: useBridge=false (CAPI leci z hooka backendu z purchase_<id_order>).
   *
   * Gdy zgody marketingowej NIE ma — event trafia do bufora (nic nie wysylamy) i zostanie
   * odtworzony, gdy user udzieli zgody. To naprawia „AddToCart nigdy nie leci" (klik zwykle
   * przed akceptacja cookies) oraz checkout-eventy odpalone przed zgoda. Zgodne z GDPR:
   * przed zgoda nic nie opuszcza przegladarki.
   */
  function fire(
    event: MetaEvent,
    pixelData: Record<string, unknown>,
    bridgeData: Record<string, unknown>,
    opts: { eventId?: string; useBridge?: boolean; noBuffer?: boolean } = {},
  ): void {
    if (typeof window === "undefined") return;

    // Kontekst zbieramy TERAZ (dostepny w handlerze), by replay nie potrzebowal Nuxt context.
    const entry: PendingMetaEntry = {
      event,
      pixelData,
      bridgeData,
      eventId: opts.eventId || genEventId(),
      useBridge: opts.useBridge !== false,
      sourceUrl: window.location?.href ?? "",
      fbp: useCookie("_fbp").value || undefined,
      fbc: useCookie("_fbc").value || undefined,
      ctx: context(),
      ts: Date.now(),
    };

    if (marketingAllowed()) {
      dispatch(entry);
      return;
    }

    // noBuffer (np. PageView — wysokoczęstotliwościowy, mało wartościowy przy replay):
    // przed zgodą po prostu pomijamy, żeby nie wypychać z bufora cennych eventów
    // (AddToCart / checkout).
    if (opts.noBuffer) return;

    // Brak zgody → buforuj (odtworzymy po zgodzie). Przytnij do MAX_PENDING (najstarsze out).
    pendingBuffer.push(entry);
    if (pendingBuffer.length > MAX_PENDING) {
      pendingBuffer.splice(0, pendingBuffer.length - MAX_PENDING);
    }
  }

  /** Odtworz zbuforowane eventy po udzieleniu zgody (wywolywane z pluginu na zmianie consent). */
  function flushPending(): void {
    if (typeof window === "undefined" || !marketingAllowed()) return;
    const now = Date.now();
    const drained = pendingBuffer.splice(0, pendingBuffer.length);
    for (const entry of drained) {
      if (now - entry.ts <= PENDING_TTL_MS) {
        dispatch(entry);
      }
    }
  }

  /** Wyczysc bufor bez wysylki (gdy user ODRZUCI zgode marketingowa — respektuj wybor). */
  function clearPending(): void {
    pendingBuffer.length = 0;
  }

  return { fire, genEventId, flushPending, clearPending };
}
